import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Plus, Trash2, Undo2 } from 'lucide-react'
import { getLocations } from '../../api/master'
import {
  cancelRefund,
  getSaleDetail,
  refundAddSku,
  refundDeleteLine,
  refundFinalize,
  refundLines,
  refundStart,
  refundUpdateLine,
} from '../../api/sales'
import ErrorBanner from '../../components/ErrorBanner'
import { ScanButton } from '../../components/BarcodeScanner'
import Spinner from '../../components/Spinner'
import { useToast } from '../../components/Toast.jsx'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDateTime } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from './salesShared'

// Refund part or all of a sale. The draft refund is only created once the
// first item is added (so just looking leaves nothing behind), and ?refund=
// resumes one started earlier.
export default function RefundProcess() {
  const nav = useNavigate()
  const { notify } = useToast()
  const { saleId } = useParams()
  const [params, setParams] = useSearchParams()
  const [refundId, setRefundId] = useState(() => num(params.get('refund')) || null)

  const [header, setHeader] = useState(null)
  const [saleLines, setSaleLines] = useState([])
  const [lines, setLines] = useState([])
  const [qtyEdits, setQtyEdits] = useState({})
  const [locations, setLocations] = useState([])
  const [locId, setLocId] = useState('')
  const [sku, setSku] = useState('')
  const [qty, setQty] = useState('1')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)
  const skuRef = useRef(null)

  const loadSale = async () => {
    const data = await getSaleDetail(saleId)
    setHeader(data?.header || null)
    setSaleLines(data?.lines || [])
    return data?.header
  }

  const loadLines = async (rid = refundId) => {
    if (!rid) { setLines([]); return }
    try {
      setLines((await refundLines(rid)) || [])
      setQtyEdits({})
    } catch {
      setLines([])
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [sale, locs] = await Promise.all([loadSale(), getLocations().catch(() => [])])
        if (cancelled) return
        const list = Array.isArray(locs) ? locs : []
        setLocations(list)
        const same = list.find((l) => l.name && l.name === sale?.location_name)
        setLocId(String((same || list[0])?.location_id || ''))
        await loadLines(refundId)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this sale.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleId])

  /** The draft refund, created on first use and kept in the URL. */
  const ensureDraft = async () => {
    if (refundId) return refundId
    const res = await refundStart(num(saleId))
    const rid = res?.refund_sale_id
    if (!rid) throw new Error('Could not start the refund.')
    setRefundId(rid)
    setParams({ refund: String(rid) }, { replace: true })
    return rid
  }

  const addItem = async (code, quantity) => {
    const q = num(quantity)
    if (!String(code || '').trim()) { setError('Scan or type the item code.'); skuRef.current?.focus(); return }
    if (q <= 0) { setError('Enter how many are coming back.'); return }
    setBusy('add')
    setError('')
    try {
      const rid = await ensureDraft()
      const res = await refundAddSku(rid, String(code).trim(), q, num(saleId))
      const current = res?.refund_sale_id || rid
      if (current !== rid) {
        setRefundId(current)
        setParams({ refund: String(current) }, { replace: true })
      }
      if (typeof res?.qty_added === 'number' && res.qty_added < q) {
        notify(`Only ${res.qty_added} could be added; the rest was already refunded.`)
      }
      await Promise.all([loadLines(current), loadSale()])
      setSku('')
      setQty('1')
    } catch (e) {
      setError(e.message || 'Could not add that item.')
    } finally {
      setBusy('')
    }
  }

  const saveQty = async (line) => {
    const raw = qtyEdits[line.line_id]
    if (raw == null || num(raw) === num(line.quantity)) return
    if (num(raw) <= 0) { setError('Use the bin to remove an item instead of setting it to zero.'); return }
    try {
      await refundUpdateLine({ refund_sale_id: refundId, line_id: line.line_id, quantity: num(raw) })
      await Promise.all([loadLines(), loadSale()])
    } catch (e) {
      setError(e.message || 'Could not change the quantity.')
    }
  }

  const removeLine = async (line) => {
    try {
      await refundDeleteLine({ refund_sale_id: refundId, line_id: line.line_id })
      await Promise.all([loadLines(), loadSale()])
    } catch (e) {
      setError(e.message || 'Could not remove that item.')
    }
  }

  const finish = async () => {
    if (!lines.length) { setError('Add the items coming back first.'); return }
    if (!num(locId)) { setError('Choose where the items are going back to.'); return }
    setBusy('finish')
    setError('')
    try {
      const res = await refundFinalize(refundId, num(locId))
      const amount = num(res?.refund_amount ?? total)
      setDone({ amount })
      notify(`Refund complete: ${money(amount)}`)
      await loadSale()
    } catch (e) {
      setError(e.message || 'Could not complete the refund.')
    } finally {
      setBusy('')
    }
  }

  const cancel = async () => {
    if (refundId && !window.confirm('Cancel this refund? Nothing will be refunded.')) return
    if (!refundId) { nav('/sales/refund-lookup'); return }
    setBusy('cancel')
    try {
      await cancelRefund(refundId, true)
      notify('Refund cancelled')
      nav('/sales/refund-lookup')
    } catch (e) {
      setError(e.message || 'Could not cancel the refund.')
      setBusy('')
    }
  }

  const lineQty = (line) => (qtyEdits[line.line_id] ?? String(line.quantity ?? ''))
  const total = lines.reduce((sum, l) => sum + num(lineQty(l)) * num(l.unit_price), 0)
  const meta = header && [fmtDateTime(header.sale_datetime), header.location_name, header.payment_method && `Paid by ${header.payment_method}`, money(header.total_amount)].filter(Boolean).join(' · ')

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/sales/refund-lookup" label="Refunds" />}
        title={header ? `Refund for ${docNo('sale', header.sale_no)}` : 'Refund'}
        meta={meta}
      />

      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading sale…" /> : done ? (
        <section className="doc-card doc-done">
          <CheckCircle2 size={28} aria-hidden="true" />
          <div>
            <h2>Refund complete</h2>
            <p>Give the customer <strong>{money(done.amount)}</strong>. The items are back in stock.</p>
          </div>
          <div className="doc-done-actions">
            <Link className="ci-btn ci-btn-ghost" to={`/sales/detail/${saleId}`}>View sale</Link>
            <Link className="ci-btn ci-btn-primary" to="/sales/refund-lookup">Back to refunds</Link>
          </div>
        </section>
      ) : (
        <>
          <section className="doc-card">
            <div className="doc-card-head">
              <h2>What was sold</h2>
              <span>Press Return for each item coming back, or scan it below.</span>
            </div>
            <div className="doc-table-scroll">
              <table className="ci-line-table">
                <thead>
                  <tr><th>Item</th><th className="num">Sold</th><th className="num">Already refunded</th><th className="num">Price</th><th /></tr>
                </thead>
                <tbody>
                  {saleLines.map((l) => {
                    const left = num(l.remaining_qty ?? l.remaining ?? (num(l.sold_qty ?? l.sold) - num(l.refunded_qty ?? l.refunded)))
                    return (
                      <tr key={l.product_id ?? l.sku}>
                        <td>{l.name}<span className="cell-sub">{l.sku}</span></td>
                        <td className="num">{num(l.sold_qty ?? l.sold)}</td>
                        <td className="num">{num(l.refunded_qty ?? l.refunded) || '–'}</td>
                        <td className="num">{money(l.unit_price)}</td>
                        <td className="num">
                          <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={left <= 0 || !!busy} onClick={() => addItem(l.sku, 1)}>
                            <Undo2 size={13} />
                            {left > 0 ? 'Return' : 'All returned'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <form className="doc-scan" onSubmit={(e) => { e.preventDefault(); addItem(sku, qty) }}>
              <label>
                <span>Scan or type an item code</span>
                <div className="scan-field">
                  <input ref={skuRef} value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU or barcode" aria-label="Scan or type an item code" />
                  <ScanButton className="ci-btn ci-btn-ghost" onScan={(code) => addItem(code, qty)} disabled={!!busy} />
                </div>
              </label>
              <label className="doc-scan-qty">
                <span>How many</span>
                <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" />
              </label>
              <button type="submit" className="ci-btn ci-btn-ghost" disabled={!!busy}><Plus size={14} />Add</button>
            </form>
          </section>

          <section className="doc-card">
            <div className="doc-card-head">
              <h2>Coming back</h2>
              <span>{lines.length ? `${lines.length} ${lines.length === 1 ? 'item' : 'items'}` : 'Nothing yet'}</span>
            </div>
            {lines.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table">
                  <thead>
                    <tr><th>Item</th><th className="num">Quantity</th><th className="num">Price</th><th className="num">Amount</th><th /></tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.line_id}>
                        <td>{l.name}<span className="cell-sub">{l.sku}</span></td>
                        <td className="num">
                          <input
                            className="doc-qty-input"
                            value={lineQty(l)}
                            inputMode="decimal"
                            aria-label={`Quantity of ${l.name}`}
                            onChange={(e) => setQtyEdits((prev) => ({ ...prev, [l.line_id]: e.target.value }))}
                            onBlur={() => saveQty(l)}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                          />
                        </td>
                        <td className="num">{money(l.unit_price)}</td>
                        <td className="num ci-total">{money(num(lineQty(l)) * num(l.unit_price))}</td>
                        <td className="num">
                          <button type="button" className="ci-form-del-btn" onClick={() => removeLine(l)} aria-label={`Remove ${l.name}`}><Trash2 size={15} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="doc-empty">Nothing is being refunded yet.</p>
            )}
          </section>

          <div className="ci-form-action-bar">
            <div className="ci-form-action-left">
              <label className="doc-inline-field">
                <span>Put the items back at</span>
                <select value={locId} onChange={(e) => setLocId(e.target.value)}>
                  {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
                </select>
              </label>
            </div>
            <div className="ci-form-action-right">
              <span className="doc-total">Refund <strong>{money(total)}</strong></span>
              <button type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={cancel}>{refundId ? 'Cancel refund' : 'Cancel'}</button>
              <button type="button" className="ci-btn ci-btn-primary" disabled={!!busy || !lines.length} onClick={finish}>
                {busy === 'finish' ? 'Completing…' : 'Complete refund'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
